import React from 'react';
import { Modal, Typography, Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const { Text, Title } = Typography;

interface DependencyItem {
  id: string;
  name?: string;
  number?: string;
  title?: string;
}

interface DeleteConfirmationModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onForceDelete?: () => void; // New prop for force delete
  title?: string;
  error?: Error & { 
    dependencies?: { type: string; items: any[] }[]; 
    blockName?: string;
    unitNumber?: string;
    amenityName?: string;
    residentName?: string;
    employeeName?: string;
  };
  onErrorDismiss?: () => void;
  isDeleting?: boolean; // External loading state control
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  onForceDelete,
  title,
  error,
  onErrorDismiss,
  isDeleting = false,
}) => {
  const { t } = useTranslation(['common', 'properties']);
  const navigate = useNavigate();

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      // Error handling is done by the parent component
      console.error('Error in delete confirmation:', error);
    }
  };

  const getDependencyTypeLabel = (type: string) => {
    switch (type) {
      case 'apartmentUnits':
        return t('properties:apartmentUnits.title');
      case 'amenities':
        return t('properties:amenity.title');
      case 'residents':
        return t('residents:title');
      case 'parkings':
        return t('properties:parkings.title');
      case 'amenityGuidelines':
        return t('properties:amenityGuidelines.title');
      case 'userAccount':
        return t('common:dependencyTypes.userAccount');
      case 'packages':
        return t('common:dependencyTypes.packages');
      case 'reservations':
        return t('common:dependencyTypes.reservations');
      case 'visitors':
        return t('common:dependencyTypes.visitors');
      case 'attendance':
        return t('common:dependencyTypes.attendance');
      case 'leaveRequests':
        return t('common:dependencyTypes.leaveRequests');
      default:
        return type;
    }
  };

  const getDependencyItemName = (item: DependencyItem) => {
    return item.name || item.number || item.title || item.id;
  };

  const getDependencyItemLink = (type: string, item: DependencyItem) => {
    switch (type) {
      case 'apartmentUnits':
        return `/resources/properties/units/${item.id}`;
      case 'amenities':
        return `/resources/properties/amenities/${item.id}`;
      case 'residents':
        return `/resources/residents/${item.id}`;
      case 'parkings':
        return `/resources/properties/parkings/${item.id}`;
      case 'amenityGuidelines':
        return `/resources/amenity-guidelines/${item.id}`;
      case 'userAccount':
        return `/users/${item.id}`;
      case 'packages':
        return `/resources/residents/packages/${item.id}`;
      case 'reservations':
        return `/resources/residents/reservations/${item.id}`;
      case 'visitors':
        return `/resources/residents/visitors/${item.id}`;
      case 'attendance':
        return `/resources/employees/attendance/${item.id}`;
      case 'leaveRequests':
        return `/resources/employees/leave-requests/${item.id}`;
      default:
        return '#';
    }
  };

  const getErrorMessage = () => {
    if (!error?.dependencies) return error?.message;

    const itemName = error.blockName || error.unitNumber || error.amenityName || error.residentName || error.employeeName;
    const itemType = error.blockName ? 'block' : error.unitNumber ? 'apartment unit' : error.residentName ? 'resident' : error.employeeName ? 'employee' : 'amenity';
    
    return `Cannot delete ${itemType} "${itemName}" because it has the following dependencies:`;
  };

  return (
    <Modal
      title={title || t('common:confirmDeleteTitle')}
      open={visible}
      onCancel={onCancel}
      footer={error?.dependencies && onForceDelete ? [
        <Button key="cancel" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button 
          key="forceDelete" 
          danger 
          type="primary"
          onClick={onForceDelete}
          loading={isDeleting}
        >
          {t('common:actions.forceDelete')}
        </Button>
      ] : [
        <Button key="cancel" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button 
          key="delete" 
          danger 
          type="primary"
          onClick={handleConfirm}
          loading={isDeleting}
        >
          {t('common:actions.delete')}
        </Button>
      ]}
      width={600}
    >
      {error?.dependencies ? (
        <div>
          <Alert
            message="Delete Failed"
            description={getErrorMessage()}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" type="link" onClick={onErrorDismiss}>
                {t('common:dismiss')}
              </Button>
            }
          />
          
          {error.dependencies.map((dependency, index) => (
            <div key={index} style={{ marginBottom: 16 }}>
              <Title level={5} style={{ marginBottom: 8 }}>
                {getDependencyTypeLabel(dependency.type)} ({dependency.items.length})
              </Title>
              <div style={{ 
                padding: '8px 12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                fontSize: '14px',
                color: '#666'
              }}>
                {dependency.items.map((item, itemIndex) => (
                  <span key={itemIndex}>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        const link = getDependencyItemLink(dependency.type, item);
                        navigate(link);
                        onCancel();
                      }}
                      style={{ padding: 0, height: 'auto', fontSize: '14px' }}
                    >
                      {getDependencyItemName(item)}
                    </Button>
                    {itemIndex < dependency.items.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>
          ))}
          
          {onForceDelete ? (
            <div style={{ marginTop: 16 }}>
              <Alert
                message="Force Delete Warning"
                description={
                  <div>
                    <p style={{ marginBottom: 8 }}>
                      <strong>⚠️ Warning:</strong> You are trying to force delete block "{error.blockName}" which has:
                    </p>
                    <div style={{ marginBottom: 8, paddingLeft: 16 }}>
                      {error.dependencies.map((dep, index) => (
                        <div key={index} style={{ marginBottom: '4px' }}>
                          <strong>{dep.items.length} {getDependencyTypeLabel(dep.type).toLowerCase()}:</strong>
                          <span style={{ marginLeft: '8px', color: '#666' }}>
                            {dep.items.map((item, itemIndex) => (
                              <span key={itemIndex}>
                                {getDependencyItemName(item)}
                                {itemIndex < dep.items.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p style={{ marginBottom: 0, fontWeight: 'bold', color: '#d46b08' }}>
                      Are you sure you want to force delete? This will permanently remove the block and ALL related data.
                    </p>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Please delete all dependent records first, then try deleting this item again.
            </Text>
          )}
        </div>
      ) : (
        <Text>{t('common:messages.confirmDelete')}</Text>
      )}
    </Modal>
  );
};

export default DeleteConfirmationModal; 